import React, { useRef, useState } from 'react';

import {
  IonContent,
  IonHeader,
  IonIcon,
  IonList,
  useIonActionSheet,
} from '@ionic/react';
import { filterOutline, searchOutline } from 'ionicons/icons';

import Button from 'components/Button';
import { SidePageRoute, useSidePage } from 'components/Modal';
import ProgressBar from 'components/ProgressBar';
import Refresher from 'components/Refresher';
import {
  SET_FILTERS,
  useScreenDispatch,
  useScreenState,
  withScreen,
} from 'components/Screen';
import TopToolbar from 'components/TopToolbar';
import { useQueryClient } from 'query';
import {
  FILTERS,
  ORDER_BY,
  ORDER_DIR,
  SEARCH_QUERY,
  SORT_LIST,
  TABLE_SETTINGS,
  initialState,
} from 'scenes/FinancePage/constants';
import screenReducer, {
  SET_TABLE_SETTINGS,
} from 'scenes/FinancePage/screenReducer';

import FRDetails from './FR/Details';
import IODetails from './IO/Details';
import SADetails from './SA/Details';
import FiltersModal from './components/FiltersModal';
import FiltersPrimary from './components/FiltersPrimary';
import List from './components/List';
import SearchToolbar from './components/SearchToolbar';
import { useIsFetching, useResetPagination } from './data';
import { grids } from './data/queryKeyFactory';
import { useIsUpdating } from './data/useIsUpdating';

const FinancePage: React.FC = () => {
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const isFetching = useIsFetching();
  const isUpdating = useIsUpdating();
  const [presentActionSheet] = useIonActionSheet();
  const resetFinancePagination = useResetPagination();
  const queryClient = useQueryClient();

  const [searchToolbarVisible, setSearchToolbarVisible] = useState<boolean>(
    !!screenState[TABLE_SETTINGS][SEARCH_QUERY]
  );

  const scrollToTop = () => {
    contentRef.current && contentRef.current.scrollToTop();
  };

  const [openFilters] = useSidePage(FiltersModal);
  const handleOpenFiltersModal = () => {
    openFilters({
      initialValues: screenState.filters,
      defaultValues: initialState[FILTERS],
      onSubmit: async (filters: any) => {
        await resetFinancePagination();
        scrollToTop();
        dispatch({
          type: SET_FILTERS,
          payload: filters,
        });
      },
    });
  };
  const openSort = async () => {
    await presentActionSheet({
      header: 'Sort by',
      translucent: true,
      buttons: SORT_LIST.map(el => {
        if (
          el.data[ORDER_BY] === screenState[TABLE_SETTINGS][ORDER_BY] &&
          el.data[ORDER_DIR] === screenState[TABLE_SETTINGS][ORDER_DIR]
        ) {
          return {
            ...el,
            role: 'selected',
          };
        }
        return el;
      }),
      onDidDismiss: async ({ detail }: any) => {
        if (detail.role === 'sort') {
          await resetFinancePagination();
          scrollToTop();
          dispatch({
            type: SET_TABLE_SETTINGS,
            payload: {
              ...screenState[TABLE_SETTINGS],
              [ORDER_BY]: detail.data[ORDER_BY],
              [ORDER_DIR]: detail.data[ORDER_DIR],
            },
          });
        }
      },
    });
  };

  const toggleShowToolbarVisible = () => {
    setSearchToolbarVisible(!searchToolbarVisible);
  };

  const handleApplyPrimaryFilters = async (filters: any) => {
    await resetFinancePagination();
    dispatch({
      type: SET_FILTERS,
      payload: filters,
    });
  };

  const handleRefresh = async () => {
    await resetFinancePagination();
    await queryClient.invalidateQueries(grids);
  };

  return (
    <>
      <SidePageRoute prefix={'/fr/'} modal={FRDetails} />
      <SidePageRoute prefix={'/io/'} modal={IODetails} />
      <SidePageRoute prefix={'/sa/'} modal={SADetails} />
      <IonHeader>
        {searchToolbarVisible ? (
          <SearchToolbar onHideToolbar={toggleShowToolbarVisible} />
        ) : (
          <TopToolbar title={'Finance'}>
            <Button onClick={toggleShowToolbarVisible}>
              <IonIcon slot={'icon-only'} icon={searchOutline} />
            </Button>
            <Button onClick={openSort}>
              <IonIcon slot={'icon-only'} name={'sort-alphabet'} />
            </Button>
            <Button onClick={handleOpenFiltersModal}>
              <IonIcon slot={'icon-only'} icon={filterOutline} />
            </Button>
          </TopToolbar>
        )}
      </IonHeader>
      {(isFetching || isUpdating) && (
        <ProgressBar type={'indeterminate'} className={'subHeaderLoader'} />
      )}
      <IonContent ref={contentRef} scrollEvents={true}>
        <Refresher onRefresh={handleRefresh} />
        <IonList lines={'full'} className={'primary-filters-container'}>
          <FiltersPrimary
            initialValues={screenState.filters}
            onSubmit={handleApplyPrimaryFilters}
          />
        </IonList>
        <List />
      </IonContent>
    </>
  );
};

export default withScreen({
  screenReducer,
  initialState,
  name: 'FinancePage',
})(React.memo(FinancePage));
